import React, { useEffect, useRef, useState } from "react";

/**
 * SmartLogo — renders any logo URL, transparently fixing SVGs whose author-set
 * viewBox is smaller than the actual artwork content (which causes the right
 * edge of taglines / letters to be clipped).
 *
 * For SVG URLs: fetches the file, measures the true content bbox via a hidden
 * DOM node, rewrites the viewBox to the bbox (plus tiny padding), and inlines
 * the corrected SVG. Falls back to a plain <img> if fetch or parsing fails.
 * For non-SVG URLs: renders <img> directly.
 */
type Props = {
  src: string;
  alt: string;
  height: number;
  className?: string;
  style?: React.CSSProperties;
};

const isSvgUrl = (u: string) => /\.svg(\?|#|$)/i.test(u);

export const SmartLogo: React.FC<Props> = ({ src, alt, height, className, style }) => {
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const holderRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    setSvgMarkup(null);
    setFailed(false);
    if (!src || !isSvgUrl(src)) return;

    let cancelled = false;
    fetch(src)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((text) => {
        if (cancelled) return;
        try {
          const doc = new DOMParser().parseFromString(text, "image/svg+xml");
          const parseErr = doc.querySelector("parsererror");
          if (parseErr) throw new Error("svg parse error");
          const svg = doc.documentElement as unknown as SVGSVGElement;
          if (!svg || svg.nodeName.toLowerCase() !== "svg") throw new Error("no svg");

          // Temporarily attach to measure the real content bbox.
          const measure = document.createElement("div");
          measure.setAttribute(
            "style",
            "position:absolute; visibility:hidden; pointer-events:none; left:-99999px; top:0; width:0; height:0;"
          );
          const clone = svg.cloneNode(true) as SVGSVGElement;
          measure.appendChild(clone);
          document.body.appendChild(measure);
          let bbox: { x: number; y: number; width: number; height: number } | null = null;
          try {
            const b = clone.getBBox();
            if (b && b.width > 0 && b.height > 0) {
              bbox = { x: b.x, y: b.y, width: b.width, height: b.height };
            }
          } catch {
            bbox = null;
          }
          document.body.removeChild(measure);

          if (bbox) {
            const pad = 2;
            svg.setAttribute(
              "viewBox",
              `${bbox.x - pad} ${bbox.y - pad} ${bbox.width + pad * 2} ${bbox.height + pad * 2}`
            );
          }
          // Force sizing to our container so the SVG scales cleanly.
          svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
          svg.setAttribute("width", "100%");
          svg.setAttribute("height", "100%");

          if (!cancelled) setSvgMarkup(new XMLSerializer().serializeToString(svg));
        } catch {
          if (!cancelled) setFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  // Non-SVG or fetch failed → plain <img>.
  if (!src || !isSvgUrl(src) || failed) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={{ height, width: "auto", display: "block", ...(style || {}) }}
      />
    );
  }

  // While loading the SVG, render a same-height, aspect-ratio-agnostic placeholder
  // so the header does not collapse.
  return (
    <span
      ref={holderRef}
      className={className}
      aria-label={alt}
      role="img"
      style={{
        display: "inline-block",
        height,
        // Let the inline SVG dictate its own width via aspect ratio.
        width: "auto",
        ...(style || {}),
      }}
    >
      {svgMarkup ? (
        <span
          style={{ display: "block", height: "100%" }}
          dangerouslySetInnerHTML={{ __html: svgMarkup }}
        />
      ) : (
        // While fetching, fall back to a raw <img> so the logo still shows
        // (even if the SVG's viewBox clips content, at least it isn't blank).
        <img
          src={src}
          alt={alt}
          style={{ height, width: "auto", display: "block" }}
        />
      )}
    </span>
  );
};

export default SmartLogo;
